import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toDateInputValue } from "@/lib/followupConstants";
import { FOLLOWUPS } from "@/constants/testIds";

const MODE_CONFIG = {
  contact: { title: "Marcar como contactada", endpoint: "contact", cta: "Guardar", notesLabel: "Notas (opcional)", notesPlaceholder: "Ej: se la contacto por WhatsApp, va a agendar la semana que viene" },
  postpone: { title: "Posponer seguimiento", endpoint: "postpone", cta: "Posponer", notesLabel: "Motivo (opcional)", notesPlaceholder: "Ej: pidio que la contactemos mas adelante" },
  discard: { title: "Descartar seguimiento", endpoint: "discard", cta: "Descartar", notesLabel: "Motivo (opcional)", notesPlaceholder: "Ej: paciente se mudo de ciudad" },
};

/** Modal compartido para las 3 acciones sobre un ciclo de seguimiento
 * (contactar/posponer/descartar). Se usa tanto en el panel de Seguimiento
 * como en la ficha del paciente (tab Tratamientos). "Reactivar" no necesita
 * modal, es una sola llamada reversible. */
export default function FollowUpActionModal({ open, onOpenChange, mode, treatment, onSuccess }) {
  const config = mode ? MODE_CONFIG[mode] : null;
  const [notes, setNotes] = useState("");
  const [newDueDate, setNewDueDate] = useState(toDateInputValue(new Date()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNotes("");
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      setNewDueDate(toDateInputValue(defaultDate));
      setError("");
    }
  }, [open, mode, treatment]);

  if (!config || !treatment) return null;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = mode === "postpone" ? { new_due_date: newDueDate, notes: notes || null } : { notes: notes || null };
      const { data } = await api.post(`/followups/${treatment.id}/${config.endpoint}`, payload);
      onSuccess(data, mode);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={FOLLOWUPS.actionModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{config.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Paciente: <span className="text-foreground font-medium">{treatment.patient_name}</span>
            {treatment.service_name ? ` \u00b7 ${treatment.service_name}` : ""}
          </p>
          {mode === "postpone" && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Nueva fecha de retorno</Label>
              <Input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} data-testid={FOLLOWUPS.actionDateInput} />
            </div>
          )}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">{config.notesLabel}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={config.notesPlaceholder} rows={3} data-testid={FOLLOWUPS.actionNotesInput} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={FOLLOWUPS.actionCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={FOLLOWUPS.actionSaveButton}>
            {saving ? "Guardando..." : config.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
